import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import withApi from '../../higherOrderComponents/withApi';
import { useSortable } from '../../../common/useSortable';
import { SortHandle, MoveButtons, sortableRowStyle, rowActionStyle } from '../../misc/SortHandle';
import {
  labelOf,
  byDisplayOrderThenName,
  byName,
  warningsFor,
  matchesFilter,
  confirmRemoval
} from './integratedResourceChecks';
import { isGuid } from '../../../next/identify';
import Select from 'react-select';
import { useFacilityProducts } from '../../../common/useFacilityProducts';

/**
 * The "New view" of a product's integrated resources: the same table as the
 * classic view, in the same style, with what it is missing.
 *
 * - Order: rows nobody has ordered come sorted by name ("Bane 2" before
 *   "Bane 10") instead of in whatever order the database returns them; dragging
 *   a row, or ↑ ↓, saves the order for everyone. Display only.
 * - Each row reads left to right as the chain the crawl follows: the court in
 *   the centre's system → the resource group → the resources the activities are
 *   shown as on WannaSport, and their product; then the locker that opens.
 * - Warnings on each row for the mistakes the table does not show.
 * - The resource group is optional and never picked from the existing ones: no
 *   two rows share a group. A name typed for a row without one makes the api
 *   create a group with one resource of that name for this product, and the
 *   crawled activities are then shown with that name instead of the title the
 *   centre sends. Left empty, they keep the centre's title.
 */

const TONE_COLOR = { bad: '#c0392b', warn: '#b9770e' };
const HEADER_STYLE = { backgroundColor: '#5e5e5e', color: '#f1f1f3' };
const ARROW_STYLE = { color: 'grey', fontSize: 20, textAlign: 'center', padding: '0 4px' };
const MUTED_STYLE = { color: 'grey', fontSize: '0.85rem' };

const Arrow = () => <td style={ARROW_STYLE} aria-hidden="true">→</td>;

// What the crawl looks at to recognise the court.
const Source = ({ row }) => (
  <>
    {row.externalId && <div><span style={MUTED_STYLE}>External id</span> <strong>{row.externalId}</strong></div>}
    {row.match && <div><span style={MUTED_STYLE}>Title matches</span> <code>{row.match}</code></div>}
    {!row.externalId && !row.match && <span style={MUTED_STYLE}>nothing</span>}
  </>
);

Source.propTypes = { row: PropTypes.object.isRequired };

const Group = ({ row }) => {
  if (!row.resourceGroupId) return <span style={MUTED_STYLE}>no resource group</span>;
  if (!row.resourceGroupName) return <span style={{ color: TONE_COLOR.bad }}>resource group not found</span>;

  return (
    <>
      <Link to={`/ResourceGroup/${row.resourceGroupId}`}><strong>{row.resourceGroupName}</strong></Link>
    </>
  );
};

Group.propTypes = { row: PropTypes.object.isRequired };

// How the activities appear on WannaSport: one per resource, named after it and
// sold through its product; without a group, one with the centre's own title.
const ShownAs = ({ row }) => {
  if (!row.resourceGroupName) return <em>the title the centre sends</em>;

  const splits = row.splits || [];
  if (splits.length === 0) return <span style={{ color: TONE_COLOR.warn }}>no split: nothing is shown</span>;

  // Usually every split holds the same court sold through another product
  // (badminton, pickleball): then the name is shown once, with its products under it.
  const resources = splits.flatMap(split => split.resources || []);
  const sameCourt = resources.length > 0
    && splits.every(split => (split.resources || []).length > 0)
    && resources.every(resource => resource.name === resources[0].name);

  if (sameCourt) {
    return (
      <>
        <strong>{resources[0].name}</strong>
        {resources.map(resource => <ProductLine key={resource.id} resource={resource} />)}
      </>
    );
  }

  return splits.map((split, splitIndex) => (
    <div key={split.id} style={{ marginBottom: 4 }}>
      {splits.length > 1 && <div style={MUTED_STYLE}>Split {splitIndex + 1}</div>}
      {(split.resources || []).length === 0
        ? <span style={MUTED_STYLE}>empty</span>
        : split.resources.map(resource => (
          <div key={resource.id}>
            <strong>{resource.name}</strong>
            <ProductLine resource={resource} />
          </div>
        ))}
    </div>
  ));
};

// The product a resource is sold through, on its own line: the arrow is joined to
// it, so it never hangs at the end of a wrapped name.
const ProductLine = ({ resource }) => (
  <div>
    <span style={MUTED_STYLE}>→</span>{' '}
    {resource.product && resource.product !== 'None'
      ? resource.product
      : <span style={{ color: TONE_COLOR.warn }}>no product</span>}
  </div>
);

ProductLine.propTypes = { resource: PropTypes.object.isRequired };

ShownAs.propTypes = { row: PropTypes.object.isRequired };

// The locker that opens for a booking: the group's own, otherwise the facility's.
const LockerCell = ({ row }) => (row.lockerName ? (
  <>
    {row.lockerName}
    <div style={MUTED_STYLE}>{row.lockerFromResourceGroup ? 'group' : 'facility'}</div>
  </>
) : <span style={MUTED_STYLE}>none</span>);

LockerCell.propTypes = { row: PropTypes.object.isRequired };

// What the last crawls made of the row over the next days, as `Stats` loads them.
const CaughtCell = ({ stats }) => {
  if (!stats) return <span style={MUTED_STYLE}>…</span>;
  if (stats.slots === 0) return <span style={{ color: TONE_COLOR.warn }}>no slot caught</span>;

  return (
    <>
      {stats.slots} slot{stats.slots === 1 ? '' : 's'}
      <div style={MUTED_STYLE}>{stats.liveActivities} live on WannaSport</div>
    </>
  );
};

CaughtCell.propTypes = { stats: PropTypes.object };

// The centre's titles no row caught: they are shown anyway with the centre's own
// title on this product, unless the crawl dropped them.
const UncaughtTitles = ({ stats }) => {
  if (!stats) return null;
  if (stats.error) return <p style={MUTED_STYLE}>Unable to load what the crawl caught: {stats.error}</p>;

  return (
    <>
      <h3 style={{ marginTop: 30 }}>Titles no row catches (next {stats.days} days)</h3>
      <p style={MUTED_STYLE}>
        Slots the centre sends that no row above recognises, from the last crawls. They are not
        renamed nor split: they show up on this product with the centre's own title. Courts the
        integration does not download at all are not counted here.
      </p>
      {stats.uncaughtTitles.length === 0 ? (
        <p>Every slot from the centre is caught by a row.</p>
      ) : (
        <table className="data-lists component-data-table cell-content-align-top data-table-no-results">
          <tbody>
            <tr style={HEADER_STYLE}>
              <td>Title the centre sends</td>
              <td>Slots</td>
              <td>Live on WannaSport</td>
            </tr>
            {stats.uncaughtTitles.map(title => (
              <tr key={title.title}>
                <td><code>{title.title || '(no title)'}</code></td>
                <td>{title.slots}</td>
                <td>
                  {title.liveActivities > 0
                    ? `${title.liveActivities}, with the centre's title`
                    : <span style={{ color: TONE_COLOR.warn }}>none</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

UncaughtTitles.propTypes = { stats: PropTypes.object };

// `resourceGroupId` is free text, as in the classic view: an id to attach a
// group, or empty for none. `newGroupName` is only used while it is empty.
// `resourceGroupName` is the name of the group the row had when the form opened.
// `soldAs` is the products a new group's court is sold as; null until changed,
// which means this page's product.
const emptyForm = { id: null, externalId: '', match: '', resourceGroupId: '', resourceGroupName: null, newGroupName: '', soldAs: null };

const IntegratedResourcesBoard = ({ api, productId, facilityId }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('');
  // The row being edited, or `emptyForm` (no id) for a new one.
  const [form, setForm] = useState(null);
  const facilityProducts = useFacilityProducts(api, facilityId);

  const load = useCallback(async () => {
    const loaded = await api.integrations.getIntegratedResources(productId);
    setRows([...(loaded || [])].sort(byDisplayOrderThenName));
  }, [api, productId]);

  useEffect(() => {
    load();
  }, [load]);

  // Loaded once with the page, not after every change: it reflects the crawls,
  // which an edit here does not rerun.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.integrations.getIntegratedResourcesStats(productId)
      .then(loaded => setStats(loaded))
      .catch(error => setStats({ error: error?.message || 'unknown error' }));
  }, [api, productId]);
  const statsByRow = useMemo(
    () => new Map((stats?.rows || []).map(row => [row.integratedResourceId, row])),
    [stats]
  );

  const saveOrder = async orderedIds => {
    const byId = new Map(rows.map(row => [row.id, row]));
    setRows(orderedIds.map((id, index) => ({ ...byId.get(id), displayOrder: index })));
    try {
      await api.integrations.reorderIntegratedResources(productId, orderedIds);
    } catch (error) {
      setMessage(`Unable to save the order: ${error?.message || 'unknown error'}`);
      load();
    }
  };

  const filtering = filter.trim() !== '';
  const sortable = useSortable({
    items: rows || [],
    getId: row => row.id,
    onReorder: saveOrder,
    enabled: !filtering && !form && !busy
  });

  const warnings = useMemo(
    () => new Map((rows || []).map(row => [row.id, warningsFor(row, rows)])),
    [rows]
  );

  if (rows === null) return <>Loading...</>;

  const run = async (fn, failText) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await load();
      return true;
    } catch (error) {
      setMessage(`${failText}: ${error?.message || 'unknown error'}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveForm = async () => {
    // The api takes either a group id or a name; a name that is not an id makes
    // it create the group on the spot, with one resource for this product. So a
    // mistyped id would turn into a group named after it: refuse it here.
    const typedId = form.resourceGroupId.trim();
    if (typedId && !isGuid(typedId)) {
      setMessage('The resource group id is not an id: copy it from the group page or from the removal prompt.');
      return;
    }
    const resourceGroupId = typedId || form.newGroupName.trim();
    // Only a new group, created from a name, is built from Sold as.
    const creatingGroup = !typedId && !!resourceGroupId;
    if (creatingGroup && form.soldAs && form.soldAs.length === 0) {
      setMessage('Choose at least one product in Sold as.');
      return;
    }

    const saved = await run(
      () => api.integrations.saveIntegratedResource(productId, {
        ...(form.id ? { id: form.id } : {}),
        externalId: form.externalId.trim(),
        match: form.match.trim(),
        resourceGroupId,
        // Untouched, the api uses this page's product on its own.
        ...(creatingGroup && form.soldAs ? { soldAs: form.soldAs.map(option => option.value) } : {})
      }),
      'Unable to save the integrated resource'
    );
    if (saved) setForm(null);
  };

  // This page's product until somebody changes the list.
  const soldAsOf = current => current.soldAs
    ?? (facilityProducts || []).filter(option => option.value === productId);

  const remove = row => {
    if (!confirmRemoval(labelOf(row), row.resourceGroupId)) return;
    run(() => api.integrations.removeIntegratedResource(row.id), 'Unable to remove the integrated resource');
  };

  const renderForm = () => (
    <form onSubmit={event => event.preventDefault()}>
      <label htmlFor="ir-external-id">ExternalId</label>
      <input
        id="ir-external-id"
        value={form.externalId}
        onChange={({ target }) => setForm({ ...form, externalId: target.value })}
      />
      <br />
      <label htmlFor="ir-match">Match Expression</label>
      <input
        id="ir-match"
        value={form.match}
        onChange={({ target }) => setForm({ ...form, match: target.value })}
      />
      <br />
      <label htmlFor="ir-group-id">
        ResourceGroupId
        {form.resourceGroupName && form.resourceGroupId.trim() === form.originalResourceGroupId && (
          <span style={{ color: 'grey' }}> (now: {form.resourceGroupName})</span>
        )}
      </label>
      <input
        id="ir-group-id"
        value={form.resourceGroupId}
        placeholder="Optional, e.g. 64b477c6-ce1c-43a8-87d4-e6d8ae6b62d3"
        onChange={({ target }) => setForm({ ...form, resourceGroupId: target.value })}
      />
      <span style={{ color: 'grey', fontSize: '0.85rem' }}>
        The resource group this row shows its activities with: they take the names and products of
        its resources. Paste a group's id to attach it, for example one left behind when its row was
        removed (the removal shows its id). Empty it to detach the group. To rename the resources or
        change their products, edit the group, linked in the table.
      </span>
      <br />
      {!form.resourceGroupId.trim() && (
        <>
          <label htmlFor="ir-new-group">Resource group name</label>
          <input
            id="ir-new-group"
            value={form.newGroupName}
            placeholder="Optional, e.g. Hal A Bane 4"
            onChange={({ target }) => setForm({ ...form, newGroupName: target.value })}
          />
          <span style={{ color: 'grey', fontSize: '0.85rem' }}>
            Optional. Empty: the activities keep the title the centre sends. With a name: they are
            shown on WannaSport with this name instead, through a new resource group with one
            resource of that name for each product in Sold as. To change the name later, rename the
            resources in the group, linked in the table.
          </span>
          {form.newGroupName.trim() && (
            <>
              <br />
              <label htmlFor="ir-sold-as">Sold as</label>
              <Select
                inputId="ir-sold-as"
                className="react-select-filters-section"
                classNamePrefix="react-select-filters-section-prefix"
                isMulti
                isLoading={!facilityProducts}
                options={facilityProducts || []}
                value={soldAsOf(form)}
                onChange={selection => setForm({ ...form, soldAs: selection || [] })}
                placeholder="Products of this centre"
              />
              <span style={{ color: 'grey', fontSize: '0.85rem' }}>
                The products this court is sold as, for example badminton and pickleball: one split
                each, with one resource named as above. Only this centre's products are listed. It
                starts with this page's product, which is wrong for a placeholder.
              </span>
            </>
          )}
        </>
      )}
      <br />
      <button
        type="button"
        style={{ float: 'left' }}
        className="button button-color-secondary button-shrink-limited"
        onClick={() => setForm(null)}
      >
        Cancel
      </button>
      <button
        type="button"
        style={{ float: 'right' }}
        className="button button-color-primary button-shrink-limited"
        disabled={busy}
        onClick={saveForm}
      >
        Save
      </button>
      <br />
      <br />
    </form>
  );

  const visible = rows.filter(row => matchesFilter(row, filter));
  const anyOrdered = rows.some(row => row.displayOrder !== null && row.displayOrder !== undefined);

  return (
    <>
      <h3>Integrated Resources ({rows.length})</h3>

      <button
        type="button"
        className="button button-color-primary button-shrink-limited"
        style={{ marginRight: 8 }}
        disabled={busy || !!form}
        onClick={() => setForm(emptyForm)}
      >
        New
      </button>
      <button
        type="button"
        className="button button-color-secondary button-shrink-limited"
        disabled={busy || rows.length < 2}
        onClick={() => saveOrder([...rows].sort(byName).map(row => row.id))}
        title="Save the rows in name order, numbers read as numbers"
      >
        Sort by name
      </button>

      <br />
      <br />

      {message && <p style={{ color: TONE_COLOR.bad }}>{message}</p>}

      {form && renderForm()}

      <label htmlFor="ir-filter">Filter</label>
      <input
        id="ir-filter"
        style={{ width: 360, maxWidth: '100%' }}
        value={filter}
        placeholder="Group, external id, regex, resource or product"
        onChange={({ target }) => setFilter(target.value)}
      />

      <p style={{ color: 'grey', fontSize: '0.85rem' }}>
        {filtering
          ? 'Clear the filter to reorder.'
          : `Drag ⠿ or use ↑ ↓ to reorder: the order is saved for everyone and only changes how the list looks.${anyOrdered ? '' : ' Nobody has ordered this list yet, so it is sorted by name.'}`}
      </p>

      {/* Collapsed borders, so the line between the rows can be drawn on the rows themselves. */}
      <table
        className="data-lists component-data-table cell-content-align-top data-table-no-results"
        style={{ borderCollapse: 'collapse' }}
      >
        <tbody>
          <tr style={HEADER_STYLE}>
            <td>#</td>
            <td>Court in the centre's system</td>
            <td />
            <td>Resource group</td>
            <td />
            <td>Shown on WannaSport as → product</td>
            <td>Locker</td>
            <td>Caught, next {stats?.days || 7} days</td>
            <td>Warnings</td>
            <td />
          </tr>
          {visible.length === 0 && (
            <tr>
              <td colSpan={10}>{rows.length === 0 ? 'No integrated resource yet.' : 'Nothing matches the filter.'}</td>
            </tr>
          )}
          {visible.map(row => {
            const index = rows.indexOf(row);

            return (
              <tr
                key={row.id}
                // A row is several lines tall: the line shows where one court ends and the next begins.
                style={{ ...sortableRowStyle(sortable, row), borderTop: '1px solid #d6d6d6' }}
                {...sortable.rowProps(row)}
              >
                <td><SortHandle sortable={sortable} item={row} position={index + 1} /></td>
                <td><Source row={row} /></td>
                <Arrow />
                <td><Group row={row} /></td>
                <Arrow />
                <td><ShownAs row={row} /></td>
                <td><LockerCell row={row} /></td>
                <td>
                  <CaughtCell
                    stats={stats && !stats.error ? (statsByRow.get(row.id) || { slots: 0, liveActivities: 0 }) : null}
                  />
                </td>
                <td>
                  {(warnings.get(row.id) || []).map(warning => (
                    <div key={warning.text} style={{ color: TONE_COLOR[warning.tone] }}>{warning.text}</div>
                  ))}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <MoveButtons sortable={sortable} item={row} index={index} count={rows.length} />
                  <div style={{ marginTop: 10 }}>
                    <span
                      style={rowActionStyle()}
                      onClick={() => !busy && !form && setForm({
                        id: row.id,
                        externalId: row.externalId || '',
                        match: row.match || '',
                        // A group that no longer exists is left out, so a new one can take its place.
                        resourceGroupId: row.resourceGroupName ? row.resourceGroupId : '',
                        originalResourceGroupId: row.resourceGroupName ? row.resourceGroupId : '',
                        resourceGroupName: row.resourceGroupName,
                        newGroupName: ''
                      })}
                    >
                      Edit
                    </span>
                    <span style={rowActionStyle(true)} onClick={() => !busy && remove(row)}>
                      Remove
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <UncaughtTitles stats={stats} />
    </>
  );
};

IntegratedResourcesBoard.propTypes = {
  api: PropTypes.object.isRequired,
  productId: PropTypes.string.isRequired,
  facilityId: PropTypes.string
};

export default withApi(IntegratedResourcesBoard);
